import { message } from "antd";
import {
  MODULE_NOT_AVAILABLE_ERROR,
  setModuleNotAvailablePayload,
} from "modules/module-not-available/constants";
import { translate, LANGUAGE } from "./general";
import { resetPostHogUser } from "posthog";

const resolveModuleUpgradePath = (upgradeUrl) => {
  if (!upgradeUrl || typeof upgradeUrl !== "string") {
    return `/${LANGUAGE}/subscriptions`;
  }
  if (upgradeUrl.startsWith("http://") || upgradeUrl.startsWith("https://")) {
    return upgradeUrl;
  }
  if (upgradeUrl.includes("subscription")) {
    return `/${LANGUAGE}/subscriptions`;
  }
  return `/${LANGUAGE}${upgradeUrl.startsWith("/") ? upgradeUrl : `/${upgradeUrl}`}`;
};

const redirectToModuleNotAvailable = (data) => {
  if (typeof window === "undefined") return;

  setModuleNotAvailablePayload({
    message: data?.message,
    upgradePath: resolveModuleUpgradePath(data?.upgrade_url),
  });

  const targetPath = `/${LANGUAGE}/module-not-available`;
  if (!window.location.pathname.endsWith("/module-not-available")) {
    window.location.assign(targetPath);
  }
};

const collectErrorMessages = (errorsPayload) => {
  const allErrors = [];
  if (Array.isArray(errorsPayload)) {
    errorsPayload.forEach((err) => {
      if (typeof err === "string") allErrors.push(err);
    });
    return allErrors;
  }

  if (errorsPayload && typeof errorsPayload === "object") {
    Object.values(errorsPayload).forEach((value) => {
      if (typeof value === "string") {
        allErrors.push(value);
      } else if (Array.isArray(value)) {
        value.forEach((err) => {
          if (typeof err === "string") allErrors.push(err);
        });
      }
    });
  }

  return allErrors;
};

const showErrorMessages = (errorsPayload) => {
  const seen = new Set();
  collectErrorMessages(errorsPayload).forEach((err) => {
    if (err && !seen.has(err)) {
      seen.add(err);
      message.error(err);
    }
  });
};

const clearAuthStorage = () => {
  if (localStorage.getItem("token") || localStorage.getItem("user")) {
    resetPostHogUser();
  }
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("central_access_token");
};

const resolveHttpStatus = (errorResponse, status) =>
  status ?? errorResponse?.status_code ?? errorResponse?.statusCode;

const resolveFallbackMessage = (errorResponse) => {
  if (typeof errorResponse?.message === "string" && errorResponse.message) {
    return errorResponse.message;
  }
  if (typeof errorResponse?.data?.message === "string") {
    return errorResponse.data.message;
  }
  if (typeof errorResponse?.data === "string") {
    return errorResponse.data;
  }
  return "";
};

/**
 * @param {object} errorResponse - the API's error envelope
 * @param {number} [status] - the HTTP status, when the caller knows it
 * @param {object} [options]
 * @param {boolean} [options.suppressNotFoundToast] - the screen renders the
 *   missing-record page itself (see `useRedirectOnNotFound`), so a 404 toast
 *   would double up. Opt in per request through `customFetch`'s options.
 */
export const handleErrors = (errorResponse, status, options = {}) => {
  const httpStatus = resolveHttpStatus(errorResponse, status);
  const errorsPayload = errorResponse?.errors ?? errorResponse?.data?.errors;
  const allErrors = collectErrorMessages(errorsPayload);

  // Handle 403 Forbidden — subscription module not in plan vs generic forbidden
  if (httpStatus === 403) {
    const data = errorResponse?.data;
    if (
      data &&
      typeof data === "object" &&
      !Array.isArray(data) &&
      data.error === MODULE_NOT_AVAILABLE_ERROR
    ) {
      redirectToModuleNotAvailable(data);
      return;
    }

    const fallbackMessage =
      typeof data === "string"
        ? data
        : typeof errorResponse?.message === "string"
          ? errorResponse.message
          : translate(
              "لا تمتلك صلاحية للقيام بتلك العملية",
              "You do not have permission to perform this action"
            );
    message.error(fallbackMessage);
    return;
  }

  // Handle 404 Not Found — only where the caller said it renders the missing
  // record itself. Everything else keeps the API's message, so the screens that
  // have not adopted the redirect are unchanged.
  if (httpStatus === 404 && options.suppressNotFoundToast) {
    return;
  }

  // Handle 401 Unauthorized and 410 Gone — show API errors (e.g. bad credentials), then clear auth
  if (httpStatus === 401 || httpStatus === 410) {
    if (allErrors.length > 0) {
      showErrorMessages(errorsPayload);
    } else {
      const fallback = resolveFallbackMessage(errorResponse);
      if (fallback) message.error(fallback);
    }
    clearAuthStorage();
    return;
  }

  if (errorResponse?.status_code === 101) {
    message.error("Outdated Version");
    message.info("Please refresh the page to update the app");
    return;
  }

  if (httpStatus === 413) {
    message.error(
      translate(
        "الملف المرفوع أكبر من الحد المسموح به",
        "The uploaded file is larger than the allowed limit"
      )
    );
    return;
  }

  if (httpStatus === 422) {
    if (allErrors.length === 0) {
      const fallback = resolveFallbackMessage(errorResponse);
      if (fallback) message.error(fallback);
      return;
    }
    showErrorMessages(errorsPayload);
    return;
  }

  if (httpStatus === 400 || httpStatus === 409) {
    if (allErrors.length > 0) {
      showErrorMessages(errorsPayload);
      return;
    }
    const fallback = resolveFallbackMessage(errorResponse);
    if (fallback) {
      message.error(fallback);
    }
    return;
  }

  if (allErrors.length > 0) {
    showErrorMessages(errorsPayload);
    return;
  }

  if (httpStatus === 500) {
    if (errorResponse?.message) {
      message.error(errorResponse.message);
    } else {
      message.error(translate("حدث خطأ فني", "Internal Server Error"));
    }
    return;
  }

  if (httpStatus === 508) {
    message.error("Time Out");
    return;
  }

  const fallback = resolveFallbackMessage(errorResponse);
  if (fallback) {
    message.error(fallback);
  }
};
